import clsx from 'clsx';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SERVICE_TYPE_AND_TIME_MODAL_ID, SERVICE_TYPE_AND_TIME_TEST_IDS } from './const';
import styles from './ServiceTypeAndTimeModal.module.scss';

import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { CloseButton } from 'components/CloseButton';
import { Modal } from 'components/Modal';
import { ScheduledOrder } from 'components/ScheduledOrder';
import { Spinner } from 'components/Spinner';
import { ServicesDropdown } from 'components/ServicesDropdown';

import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { useMerchant } from 'hooks/context/merchant';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useServiceType } from 'hooks/context/serviceType';
import { ServiceType, ServiceTypeLabel } from 'hooks/context/serviceType/models';
import { useDelivery } from 'hooks/context/Delivery';
import { useScheduledOrderDisplay } from 'hooks/context/ScheduledOrderDisplay';
import { useScheduledOrderMethods } from 'hooks/context/ScheduledOrderMethods';
import Typography from 'components/Typography/Typography';

import { RouteFC } from 'util/routeTypes';
import { CUSTOMER_INFO_TEST_ID } from '../../components/ContactInfoForm.const';
import { formatAddress } from '../../util/Formatting';
import { useGetLatestAddress } from 'hooks/useApi/useAddress';
import { useDeliveryValidation } from 'hooks/useDeliveryValidation';
import { useNavigate } from '@reach/router';

export type ServiceTypeProps = {
  onClose: () => void;
  headerLabel?: string;
  subheaderLabel?: string;
  headerClass?: string;
  isScheduledOrderModal?: boolean;
};

export const EditServiceTypeAndTimeModal: RouteFC = () => {
  const { currentServiceType } = useServiceType();

  const navigate = useNavigate();
  const closeModal = useCallback(() => navigate('./'), [navigate]);

  useEffect(() => {
    if (currentServiceType) return;

    closeModal();
  }, [closeModal, currentServiceType]);

  return <ServiceTypeAndTimeModal onClose={closeModal} />;
};

export const ServiceTypeAndTimeModal = function ServiceTypeAndTimeModal({ onClose, headerLabel, subheaderLabel, headerClass, isScheduledOrderModal=false
}: ServiceTypeProps) {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { merchant, isLoading } = useMerchant();
  const { deliveryAddress, defaultAddressHasBeenLoaded } = useDelivery();
  const { isAcceptingOrders, showScheduledOrderState } = useMerchantState();
  const { update, serviceTypes, chosenServiceType, setChosenServiceType, currentServiceType } = useServiceType();
  const { updateScheduledOrder } = useScheduledOrderMethods();
  const { displayValueDate, displayValueTime, resetDisplayValueState, resetScheduledOrderToInitialValues, resetToFirstAvailableTime } = useScheduledOrderDisplay();

  const header = headerLabel || t('editOrder');

  const { asyncValidateAndSetDefaultAddress } = useDeliveryValidation();
  const serverDefaultAddress = useGetLatestAddress();
  const defaultAddress = serverDefaultAddress.data;

  const navigateToAddressModal = useCallback(() => {
    const path = isScheduledOrderModal ? '~address' : '~checkout/address';
    navigate(path,
        {
          replace: true,
          state: {
            isFromScheduledOrderModal: true
          }
        });
  }, [navigate, isScheduledOrderModal]);

  const submit = useCallback(() => {
    displayValueDate && displayValueTime && updateScheduledOrder({ currentDate: displayValueDate.dateTime, currentTime: displayValueTime.dateTime });
    chosenServiceType && update(chosenServiceType);
    onClose();
    if (!displayValueTime && !displayValueDate) resetScheduledOrderToInitialValues();
  }, [chosenServiceType, displayValueTime, displayValueDate, resetScheduledOrderToInitialValues, onClose, update, updateScheduledOrder]);

  // Reset to initial choices when modal closes/cancels
  const handleOnClose = useCallback(() => {
    if (isScheduledOrderModal) {
      resetScheduledOrderToInitialValues();
    } else {
      resetDisplayValueState();
    }

    setChosenServiceType(currentServiceType);
    onClose();
  }, [isScheduledOrderModal, onClose, resetDisplayValueState, resetScheduledOrderToInitialValues, setChosenServiceType, currentServiceType]);

  const handleSelect = useCallback((selectedService: ServiceTypeLabel) => {
    setChosenServiceType(selectedService.name);
    if (selectedService.name === ServiceType.DELIVERY && !deliveryAddress) {
      if (defaultAddress && !defaultAddressHasBeenLoaded){
        asyncValidateAndSetDefaultAddress(defaultAddress, { replacePath: true });
      }
      navigateToAddressModal();
    }
    resetToFirstAvailableTime();
  }, [navigateToAddressModal, setChosenServiceType, deliveryAddress, resetToFirstAvailableTime, defaultAddress, defaultAddressHasBeenLoaded, asyncValidateAndSetDefaultAddress]);

  return (
    <Modal
      className={styles.modal}
      isOpen={true}
      onClose={handleOnClose}
      aria={{ labelledby: SERVICE_TYPE_AND_TIME_MODAL_ID }}
    >
      <div className={styles.container} data-testid={SERVICE_TYPE_AND_TIME_TEST_IDS.root}>
        <header className={styles.modalHeader}>
          {!headerClass && <Typography.Heading1 id={SERVICE_TYPE_AND_TIME_MODAL_ID} >{header}</Typography.Heading1>}
          {headerClass && <h2 className={clsx(headerClass)} id={SERVICE_TYPE_AND_TIME_MODAL_ID}>{header}</h2>}
          <CloseButton onClick={handleOnClose} />
        </header>
        {subheaderLabel && <div className={styles.subHeader}>
          <div>{ subheaderLabel }</div>
        </div>}

        <section className={styles.body}>
          <div className={clsx(styles.editServiceType)}>
            {serviceTypes.length > 1 &&
              <ServicesDropdown currentServiceType={chosenServiceType} handleSelect={handleSelect} />
            }
          </div>
          {showScheduledOrderState && deliveryAddress && chosenServiceType === ServiceType.DELIVERY &&
          <div className={styles.deliveryText}>
            <Typography.Body>
              {t('deliverTo', { address: formatAddress(deliveryAddress) })}
            </Typography.Body>
            <Button
                variant={ButtonVariant.TEXT}
                className={clsx(styles.editButton)}
                onClick={() => {
                  navigateToAddressModal();
                }}
                data-testid={CUSTOMER_INFO_TEST_ID.deliveryAddressEdit}
            >
          {t('edit')}
        </Button>
          </div>
          }
          {showScheduledOrderState &&
            <div className={styles.scheduledOrderWrapper}>
              <ScheduledOrder selectClassName={ styles.scheduledOrderDropdownSelect } />
            </div>
          }
        </section>

      </div>
      <footer className={clsx(styles.bottomTray, { [styles.acceptingOrders]: !isLoading && isAcceptingOrders })}>
        {isLoading && <Spinner />}
        {!isLoading && !isAcceptingOrders && !isScheduledOrderModal && t('bannerMessage.offline', { merchantName: merchant?.name })}
        {!isLoading && isAcceptingOrders && !isScheduledOrderModal && (
          <>
            <Button
              type="button"
              className={clsx('h3', styles.cancel)}
              variant={ButtonVariant.SECONDARY}
              onClick={handleOnClose}
              data-testid={SERVICE_TYPE_AND_TIME_TEST_IDS.cancel}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              className={clsx('h3', styles.submit)}
              onClick={submit}
              data-testid={SERVICE_TYPE_AND_TIME_TEST_IDS.submit}
            >
              {t('done')}
            </Button>
          </>
        )}
        {!isLoading && isAcceptingOrders && isScheduledOrderModal && (
            <Button
            type="submit"
            className={clsx('h3', styles.submit, styles.scheduledOrderButton)}
            onClick={submit}
            variant={ButtonVariant.PRIMARY}
            data-testid={SERVICE_TYPE_AND_TIME_TEST_IDS.submit}
          >
            {t('scheduledOrder.button')}
          </Button>
        )}
      </footer>
    </Modal>
  );
};
